import { makeApiRequestPromise } from "../../lib/api";
import { toFriendlyApiError } from "../../lib/apiError";
import type { ClusteringMapResponse, Granularity, TraceDetail } from "./types";

/**
 * Fetch the failure-mode clustering map for the authenticated user's domain.
 *
 * Read-only; the backend aggregates `clustering_judgement` over a calendar
 * window — today (daily) / the current ISO week (weekly) *to date* on the live
 * view, or the complete day/week when scrubbed — no clustering happens at
 * request time. A domain with no clustering data returns the cold-start shape
 * (empty `modes`/`points`, `last_discovery_at` = null).
 *
 * `asOf` (UTC ISO 8601) moves the window's end-point back through history for
 * the time-slider — the past window's traffic rendered through today's
 * taxonomy. Omitted ⇒ the live "now" (period-to-date) view.
 */
export const getClusteringMap = async (
  granularity: Granularity,
  asOf?: string,
): Promise<ClusteringMapResponse> => {
  try {
    const params: Record<string, string> = { granularity };
    if (asOf) params.as_of = asOf;
    const response = await makeApiRequestPromise({
      method: "GET",
      path: "/api/clustering/map",
      params,
    });
    return response as ClusteringMapResponse;
  } catch (error: unknown) {
    throw toFriendlyApiError(error, "Failed to load failure clusters.");
  }
};

/**
 * Fetch one trace (full content + evaluations) by id for the authenticated
 * user's domain. Used to enrich the coding-agent brief built from a failure
 * mode's example points (#1324), which carry only `trace_id` + the judge's
 * explanation. Reuses the same `/api/v1/traces/{id}` endpoint the Traces
 * deep-link uses (#1278), so it is already domain-scoped and ZDR-aware
 * server-side (a cross-domain / unknown id 404s, surfaced to the caller).
 *
 * Intentionally a local twin of insights' `getTrace` (same endpoint) rather than
 * an import: failure-clusters stays decoupled from insights. If this endpoint's
 * path or contract changes, update both call sites (and `TraceDetail`).
 */
export const fetchTraceForBrief = async (
  traceId: string,
): Promise<TraceDetail> => {
  try {
    const response = await makeApiRequestPromise({
      method: "GET",
      path: `/api/v1/traces/${encodeURIComponent(traceId)}`,
    });
    return response as TraceDetail;
  } catch (error: unknown) {
    throw toFriendlyApiError(error, "Failed to load trace for the brief.");
  }
};

/**
 * The evaluator returned by `POST /api/v1/evaluators`. The endpoint returns the
 * full evaluator; we type only the `group_id` this feature consumes to deep-link
 * to the new evaluator's detail page (#1399).
 */
export interface CreatedEvaluator {
  group_id: string;
}

/**
 * Create an evaluator from a criteria string, returning its new group id so a
 * promoted failure mode can jump straight to the evaluator's detail page
 * (#1399). A deliberate local twin of the evaluators feature's `createEvaluator`
 * (same `POST /api/v1/evaluators` contract) — just as `fetchTraceForBrief`
 * mirrors insights' `getTrace` — so failure-clusters keeps no cross-feature
 * dependency. Keep in sync if that endpoint's contract changes.
 */
export const createEvaluatorFromCriteria = async (
  criteria: string,
): Promise<CreatedEvaluator> => {
  try {
    const response = await makeApiRequestPromise({
      method: "POST",
      path: "/api/v1/evaluators",
      body: { criteria },
    });
    return response as CreatedEvaluator;
  } catch (error: unknown) {
    throw toFriendlyApiError(error, "Failed to create evaluator from failure mode.");
  }
};
